import { useMemo, useState } from "react";

import {
  ResponsiveDataTable,
  type ResponsiveColumn,
} from "../../../../ui/responsive-table/index.ts";
import type { SortDirection } from "../../../../ui/responsive-table/types.ts";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import type { Holiday, HolidayTableRow } from "../../types.ts";
import { RegionBadge } from "../region-badge/region-badge.tsx";

import styles from "./holidays-table.module.css";

/**
 * Parse API date strings (often `YYYY-MM-DD`) as **local** calendar dates.
 * `new Date("2024-06-15")` is UTC midnight and can shift to the previous local day,
 * which breaks weekday labels and sort order for the Day column.
 */
function parseLocalDate(dateStr: string): Date | null {
  if (dateStr == null || String(dateStr).trim() === "") return null;
  const s = String(dateStr).trim();
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]) - 1;
    const d = Number(ymd[3]);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
      return null;
    const local = new Date(y, m, d);
    return Number.isFinite(local.getTime()) ? local : null;
  }
  const parsed = new Date(s);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function parseHolidayDateMs(dateStr: string): number {
  const d = parseLocalDate(dateStr);
  return d ? d.getTime() : 0;
}

function formatShortDayMonth(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  if (!d) return dateStr || "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatWeekday(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", { weekday: "long" });
}

const COLUMNS: ResponsiveColumn<HolidayTableRow>[] = [
  {
    id: "date",
    header: "Date",
    accessor: "date",
    sortable: true,
    getSortValue: (row) => parseHolidayDateMs(row.date),
    render: (row) => (
      <span className={styles.datePill}>{formatShortDayMonth(row.date)}</span>
    ),
  },
  {
    id: "nameOfHoliday",
    header: "Holiday name",
    accessor: "nameOfHoliday",
    sortable: true,
  },
  {
    id: "region",
    header: "Holiday Scope",
    accessor: "region",
    sortable: true,
    render: (row) => <RegionBadge region={row.region} />,
  },
  {
    id: "day",
    header: "Day",
    accessor: "date",
    sortable: true,
    /** Alphabetical by weekday label (e.g. Friday, Monday), not calendar date. */
    getSortValue: (row) => formatWeekday(row.date),
    render: (row) => formatWeekday(row.date),
  },

];

export interface HolidaysTableProps {
  rows: Holiday[];
  emptyMessage?: string;
}

export function HolidaysTable({ rows, emptyMessage }: HolidaysTableProps) {
  const [sortKey, setSortKey] = useState<string>("date");
  const [sortDir, setSortDir] = useState<Exclude<SortDirection, null>>("asc");

  const data = useMemo(
    () => rows.map((r) => ({ ...r }) as HolidayTableRow),
    [rows],
  );

  const handleSort = (key: string | undefined, dir: SortDirection) => {
    if (key == null || dir == null) {
      setSortKey("date");
      setSortDir("asc");
      return;
    }
    setSortKey(key);
    setSortDir(dir);
  };

  return (
    <ResponsiveDataTable<HolidayTableRow>
      columns={COLUMNS}
      data={data}
      sortKey={sortKey}
      sortDir={sortDir}
      onSortChange={handleSort}
      aria-label="Holidays"
      pagination={false}
      emptyState={
        <EmptyStateMessage
          message={emptyMessage ?? "No holidays match this view."}
        />
      }
    />
  );
}
